import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { toast } from "../utils/toast";
import { bursariesApi } from "../api/hliLists";
import { errorMessageOf } from "../api/scholarships";
import { BURSARIES_KEY, nameOf } from "./bursaries/shared";
import { claimDisplayStatus as displayStatus } from "../config/claimStatus";
import { Button, Container, PageHeader, StatusBadge, Table } from "./ui";

const PAGE_SIZE = 10;

// Claims submitted by the students of the signed-in HLI admin's institution
export default function HliStudentClaims() {
  const [page, setPage] = useState(0);

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: [BURSARIES_KEY, "result-claims", "mine", page],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      try {
        return (await bursariesApi.institutionResultClaims({ page, size: PAGE_SIZE })).data;
      } catch (error) {
        console.error("Failed to load claims:", error);
        toast.error(errorMessageOf(error, "Failed to load claims."), { id: "hli-claims-error" });
        throw error;
      }
    },
  });

  const rows = (data?.items ?? []).map((c, i) => ({ ...c, index: page * PAGE_SIZE + i + 1 }));

  const columns = [
    { key: "index", label: "#", className: "text-slate-500" },
    { key: "registrationNumber", label: "Reg number", className: "font-semibold text-slate-900" },
    { key: "student", label: "Student", accessor: nameOf },
    { key: "claimType", label: "Claim type" },
    { key: "description", label: "Description", render: (c) => <span className="line-clamp-2 max-w-xs">{c.description || "—"}</span> },
    { key: "academicYear", label: "Academic year", accessor: (c) => c.academicYear?.name },
    {
      key: "status",
      label: "Status",
      accessor: (c) => displayStatus(c.status),
      render: (c) => <StatusBadge status={displayStatus(c.status)} />,
    },
    { key: "createdAt", label: "Submitted", accessor: (c) => (c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"), className: "whitespace-nowrap" },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Student Claims" subtitle="Claims submitted by students in your institution" />
      <Table cardView
        columns={columns}
        data={rows}
        isLoading={isPending}
        searchPlaceholder="Search this page"
        exportName="hli-student-claims"
        emptyTitle="No claims found"
        actions={
          <Button variant="ghost" icon={<ArrowPathIcon className="h-4 w-4" />} onClick={() => refetch()} loading={isFetching && !isPending}>
            Refresh
          </Button>
        }
        pagination={{ page, totalPages: Math.ceil((data?.total ?? 0) / PAGE_SIZE), totalElements: data?.total ?? rows.length, pageSize: PAGE_SIZE, onPageChange: setPage }}
      />
    </Container>
  );
}
